import React from "react";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import {
  getProfileCityOptions,
  getProfileCountry,
  normalizeProfileCity,
} from "../../constants/profileLocations";

const DEFAULT_CITY_COUNTRY = "SK";

export function buildEventSubscriptionForm({
  selectedFilters,
  codeValue,
  eventType,
  cityValue,
}) {
  const activeFilters = new Set(selectedFilters || []);
  const form = new FormData();
  form.append("subscribed_to", activeFilters.has("CODE") ? codeValue.trim() : "");
  form.append("event_type", activeFilters.has("CATEGORY") ? eventType : "");
  form.append("city", activeFilters.has("CITY") ? cityValue.trim() : "");
  return form;
}

export function isEventSubscriptionReady({ selectedFilters, codeValue, eventType, cityValue }) {
  const filters = selectedFilters || [];
  return filters.length > 0 && filters.every((filter) => {
    if (filter === "CODE") return /^[A-Z0-9]{3}$/.test((codeValue || "").trim().toUpperCase());
    if (filter === "CATEGORY") return Boolean(eventType);
    if (filter === "CITY") return Boolean((cityValue || "").trim());
    return false;
  });
}

function getCityOptionsForProfile(profile) {
  const country = getProfileCountry(profile?.country)?.value || DEFAULT_CITY_COUNTRY;
  const profileCity =
    normalizeProfileCity(country, profile?.city) ||
    String(profile?.city || "").split(",")[0].trim();
  const options = getProfileCityOptions(country);

  if (
    profileCity &&
    !options.some(
      (option) => option.value.toLocaleLowerCase() === profileCity.toLocaleLowerCase()
    )
  ) {
    options.unshift({ value: profileCity, label: profileCity });
  }

  return { options, profileCity, country };
}

const SubscriptionModal = ({ isOpen, onClose, initialSubscriptions = [] }) => {
  const [selectedFilters, setSelectedFilters] = React.useState(["CODE"]);
  const [codeValue, setCodeValue] = React.useState("");
  const [isCodeFocused, setIsCodeFocused] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);

  // New state variable to store subscription slots
  const [subscriptions, setSubscriptions] = React.useState([]);

  const { t } = useTranslation("events");

  // Helper to refresh subscriptions
  const fetchSubscriptions = async () => {
    try {
      const { data } = await api.get("events/user-subscriptions/");
      const mapped = data.subscriptions.map(sub => ({
        id: sub.id,
        code: sub.subscribed_to_code || "",
        category: sub.event_type || "",
        city: sub.city || ""
      }));
      setSubscriptions(mapped);
    } catch (err) {
      console.error("Failed to reload subscriptions", err);
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    // Initialize from props passed in
    const mapped = initialSubscriptions.map(sub => ({
      id: sub.id,
      code: sub.subscribed_to_code || "",
      category: sub.event_type || "",
      city: sub.city || ""
    }));
    setSubscriptions(mapped);
  }, [isOpen, initialSubscriptions]);

  const categoryOptions = React.useMemo(() => [
    { value: "sport", label: t("event_create.category_grid.sport") },
    { value: "recreation", label: t("event_create.category_grid.recreation") },
    { value: "party", label: t("event_create.category_grid.party") },
    { value: "education", label: t("event_create.category_grid.education") },
    { value: "traveling", label: t("event_create.category_grid.traveling") },
  ], [t]);
  const [selectedCategoriesInput, setSelectedCategoriesInput] = React.useState([]);

  const [cityOptions, setCityOptions] = React.useState(() =>
    getProfileCityOptions(DEFAULT_CITY_COUNTRY)
  );
  const [cityValue, setCityValue] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    api
      .get("/accounts/profile/", {
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const { options, country } = getCityOptionsForProfile(data);
        setCityOptions(options);
        api.get("/accounts/locations/cities/", {
          params: { q: "", country },
          authOptional: true,
          suppressUnauthorizedRedirect: true,
        }).then(({ data: catalogue }) => {
          if (cancelled) return;
          const remote = Array.isArray(catalogue?.cities) ? catalogue.cities : [];
          const seen = new Set();
          setCityOptions([
            ...remote.filter((city) => city.country === country).map((city) => ({
              value: city.name,
              label: city.name,
            })),
            ...options,
          ].filter((option) => {
            const key = option.value.toLocaleLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }));
        }).catch(() => {
          // Keep bundled suggestions when the catalogue is temporarily down.
        });
      })
      .catch(() => {
        if (cancelled) return;
        const options = getProfileCityOptions(DEFAULT_CITY_COUNTRY);
        setCityOptions(options);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const eventType = selectedCategoriesInput[0] || "";
  const canAdd = isEventSubscriptionReady({ selectedFilters, codeValue, eventType, cityValue });

  const handleAddSubscription = async () => {
    if (subscriptions.length >= 3 || isAdding || !canAdd) return;
    try {
      setIsAdding(true);
      const form = buildEventSubscriptionForm({
        selectedFilters,
        codeValue,
        eventType,
        cityValue,
      });
      await api.post("events/add-subscription/", form);
      await fetchSubscriptions();
      setCodeValue("");
      setSelectedCategoriesInput([]);
      setCityValue("");
    } catch (err) {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message:
            err.response?.data?.error ||
            t("subscription_modal.errors.add_failed")
        }
      }));
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AppModalShell
      open={isOpen}
      title={t("subscription_modal.title")}
      onClose={onClose}
      closeAriaLabel={t("common.close", { defaultValue: "Close" })}
      maxWidthClassName="max-w-xl"
      desktopMaxHeight="min(88vh, 720px)"
      layout
      bodyClassName={isCodeFocused ? "overflow-y-hidden pb-3" : ""}
      footer={
        <AppModalButton type="button" onClick={handleAddSubscription} disabled={subscriptions.length >= 3 || !canAdd} loading={isAdding}>
          {t("subscription_modal.buttons.add")}
        </AppModalButton>
      }
    >
        {/* Filter buttons */}
        <div className="flex justify-center gap-2 mb-5">
          {["CODE", "CATEGORY", "CITY"].map((label) => {
            const isActive = selectedFilters.includes(label);
            return (
              <button
                key={label}
                onClick={() => {
                  if (isActive) {
                    // не позволяем снять последнюю активную
                    if (selectedFilters.length > 1) {
                      setSelectedFilters(selectedFilters.filter(f => f !== label));
                      if (label === "CODE") {
                        setCodeValue("");
                      } else if (label === "CATEGORY") {
                        setSelectedCategoriesInput([]);
                      } else if (label === "CITY") {
                        setCityValue("");
                      }
                    }
                  } else {
                    setSelectedFilters([...selectedFilters, label]);
                  }
                }}
                className={`min-w-0 flex-1 h-8 px-2 rounded-full border text-[13px] font-semibold transition-all duration-200 ${
                  isActive
                    ? "border-white text-white"
                    : "border-white border-opacity-40 text-white text-opacity-40"
                }`}
              >
                {t(`subscription_modal.filters.${label.toLowerCase()}`)}
              </button>
            );
          })}
        </div>

        {/* Dynamic input fields (always packed from the top) */}
        <div className="flex flex-col gap-3 mb-3">
          {/* CODE field */}
          {selectedFilters.includes("CODE") && (
            <div
              className="w-full h-10 bg-white/20 rounded-lg flex items-center px-3 text-sm cursor-text"
              onClick={() => document.getElementById("code-input")?.focus()}
            >
              <input
                id="code-input"
                type="text"
                maxLength={3}
                aria-label={t("subscription_modal.code.label")}
                placeholder={t("subscription_modal.code.label")}
                value={codeValue}
                onFocus={() => setIsCodeFocused(true)}
                onBlur={() => setIsCodeFocused(false)}
                onChange={(e) => {
                  const input = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "");
                  setCodeValue(input);
                }}
                className="w-full min-w-0 bg-transparent outline-none text-white font-normal placeholder-white/40"
              />
            </div>
          )}

          {/* CATEGORY field */}
          {selectedFilters.includes("CATEGORY") && (
            <div className="relative">
              <select
                aria-label={t("subscription_modal.filters.category")}
                value={selectedCategoriesInput[0] || ""}
                onChange={(event) =>
                  setSelectedCategoriesInput(event.target.value ? [event.target.value] : [])
                }
                className={`h-10 w-full appearance-none rounded-lg bg-white/20 px-3 pr-10 text-sm outline-none focus:ring-1 focus:ring-white/60 ${
                  selectedCategoriesInput.length ? "text-white" : "text-white/40"
                }`}
              >
                <option value="" disabled className="text-black">
                  {t("subscription_modal.category.placeholder")}
                </option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value} className="text-black">
                    {option.label}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70"
                fill="none"
              >
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}

          {/* CITY field */}
          {selectedFilters.includes("CITY") && (
            <div className="relative">
              <select
                aria-label={t("subscription_modal.filters.city")}
                value={cityValue}
                onChange={(event) => setCityValue(event.target.value)}
                className={`h-10 w-full appearance-none rounded-lg bg-white/20 px-3 pr-10 text-sm outline-none focus:ring-1 focus:ring-white/60 ${
                  cityValue ? "text-white" : "text-white/40"
                }`}
              >
                <option value="" disabled className="text-black">
                  {t("subscription_modal.city.placeholder")}
                </option>
                {cityOptions.map((option) => (
                  <option key={option.value} value={option.value} className="text-black">
                    {option.label}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70"
                fill="none"
              >
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>

        {!isCodeFocused && (
          <>
            {/* Divider */}
            <div className="w-full h-px bg-white opacity-50 mb-3" />

            {/* Subs count */}
            <div className="text-white text-base text-center font-semibold mb-4">
              {t("subscription_modal.subs.count", {
                count: subscriptions.length,
                max: 3,
              })}
            </div>

            {/* Subscription slots */}
            <div className="flex gap-4 mb-6">
              {Array.from({ length: 3 }).map((_, index) => {
                if (index < subscriptions.length) {
                  const sub = subscriptions[index];
                  return (
                    <div
                      key={index}
                      className="min-w-0 flex-1 h-20 rounded-2xl bg-white text-blue-600 px-1 py-1 text-sm font-semibold relative flex justify-center items-center"
                    >
                      <button
                        className="absolute top-1 right-2 text-base text-blue-600"
                        onClick={async () => {
                          try {
                            await api.post(`events/remove-subscription/${sub.id}/`);
                            await fetchSubscriptions();
                          } catch (err) {
                            window.dispatchEvent(new CustomEvent("global-error", {
                              detail: {
                                message:
                                  err.response?.data?.error ||
                                  t("subscription_modal.errors.remove_failed"),
                              },
                            }));
                          }
                        }}
                      >
                        ×
                      </button>
                      <div className="flex flex-col items-center justify-center gap-1 text-center leading-snug text-xs w-full px-0">
                        {sub.code && <div className="truncate w-full">{sub.code}</div>}
                        {sub.category && <div className="truncate w-full">{categoryOptions.find((option) => option.value === sub.category)?.label || sub.category.charAt(0).toUpperCase() + sub.category.slice(1)}</div>}
                        {sub.city && <div className="truncate w-full">{sub.city}</div>}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    className="min-w-0 flex-1 h-20 rounded-2xl border-2 border-white opacity-25 flex items-center justify-center text-white font-semibold"
                  >
                    {t("subscription_modal.slots.empty")}
                  </div>
                );
              })}
            </div>
          </>
        )}

    </AppModalShell>
  );
};

export default SubscriptionModal;
